import DOMPurify from "dompurify";
import HTMLReactParser, {
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import React, { ReactNode } from "react";

interface ParserUtilProp extends HTMLReactParserOptions {
  marked?: boolean;
}

let initialized = false;
const DOMConfig: DOMPurify.Config = {
  USE_PROFILES: { html: true },
  FORBID_ATTR: ["onerror", "onclick"],
  FORBID_TAGS: [
    "style",
    "script",
    "input",
    "button",
    "textarea",
    "meta",
    "link",
    "head",
    "title",
  ],
  ALLOW_DATA_ATTR: true,
  CUSTOM_ELEMENT_HANDLING: {
    // @ts-ignore
    tagNameCheck(tagName) {
      return tagName.match(/^file-/);
    },
    // @ts-ignore
    attributeNameCheck(attrName) {
      return attrName === "timestamp";
    },
    allowCustomizedBuiltInElements: true,
  },
};

function configureDOMPurify() {
  // https://github.com/cure53/DOMPurify/tree/main/demos#hook-to-open-all-links-in-a-new-window-link
  DOMPurify.addHook("afterSanitizeAttributes", function (node) {
    if ("target" in node) {
      node.setAttribute("target", "_blank");
      // https://cwe.mitre.org/data/definitions/1022.html
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
}

export function convertNodeToElement(node: Element): ReactNode | null {
  if (node.type === "tag") {
    return React.createElement(
      node.name,
      { key: node.attribs?.key || undefined },
      node.children,
    );
  }
  return null;
}

/**
 * Regex pattern to match <pre><code>...</code></pre> blocks.
 * Does NOT match standalone <code>...</code> (inline code).
 */
export const CODE_BLOCK_REGEX =
  /<pre([^>]*)>\s*<code([^>]*)>([\s\S]*?)<\/code>\s*<\/pre>/gi;

/**
 * Escapes HTML entities to prevent them from being interpreted as HTML.
 */
export function escapeHtmlEntities(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Checks if content contains unescaped HTML tags (< followed by a letter).
 * This distinguishes between raw HTML like <html> and already-escaped &lt;html&gt;
 */
function containsUnescapedHtmlTags(content: string): boolean {
  // Match < followed by a letter (start of HTML tag) or </ (closing tag)
  return /<[a-zA-Z/]/.test(content);
}

/**
 * Escapes HTML entities inside <pre><code> blocks before DOMPurify sanitization.
 * This prevents DOMPurify from stripping HTML tags that should be displayed
 * as text inside code blocks (e.g., <html>, <script>, etc.)
 *
 * Only escapes content inside <pre><code>...</code></pre> (code blocks),
 * NOT standalone <code>...</code> (inline code) to preserve normal inline formatting.
 *
 * Only escapes if the content contains unescaped HTML tags to prevent
 * double-escaping already-escaped content.
 *
 * @param html - The HTML string to process
 * @returns HTML string with code block contents escaped
 */
export function escapeCodeBlocksBeforeSanitize(html: string): string {
  if (!html) return html;

  // Reset regex lastIndex since it's a global regex
  CODE_BLOCK_REGEX.lastIndex = 0;

  return html.replace(
    CODE_BLOCK_REGEX,
    (_match, preAttrs, codeAttrs, content) => {
      // Only escape if content contains unescaped HTML tags
      // This prevents double-escaping already-escaped content
      if (containsUnescapedHtmlTags(content)) {
        const escapedContent = escapeHtmlEntities(content);
        return `<pre${preAttrs}><code${codeAttrs}>${escapedContent}</code></pre>`;
      }
      // Content is already escaped or contains no HTML, return as-is
      return `<pre${preAttrs}><code${codeAttrs}>${content}</code></pre>`;
    },
  );
}

export const purifyHtml = (html: string) => {
  return DOMPurify.sanitize(html, DOMConfig) as string;
};

export default function parseHtml(html: string, options?: ParserUtilProp) {
  if (!initialized) {
    configureDOMPurify();
    initialized = true;
  }

  if (html === null || html === undefined) return "";

  // Escape HTML inside code blocks before sanitization to preserve code content
  const escapedHtml = escapeCodeBlocksBeforeSanitize(html);

  const content = purifyHtml(escapedHtml) as string;

  return HTMLReactParser(content, options);
}
